import { api } from "./client";
import type {
  Contact,
  ContactDetail,
  Campaign,
  CampaignSendDetail,
  PaginatedResponse,
  WebEvent,
  CallCampaign,
  CallResult,
  Sequence,
  SequenceEnrollment,
  SmsTemplate,
  LineTemplate,
  LineMessage,
  FaxReceived,
  MetaAudience,
  MetaConversionLog,
  MetaSettings,
  Project,
  TimelineResponse,
  DoNotCallEntry,
  Prospect,
  ProspectCallQueueResponse,
  ProspectRefreshCandidatesResponse,
  ProspectSegmentFilter,
  AudienceType,
  SegmentFilter,
  ProspectExecutableSummary,
  ChannelExportImportSummary,
  ChannelExportImportResult,
  AudienceList,
  AudienceListMember,
} from "@/types";

export const authApi = {
  appConfig: () =>
    api.get<{ appName: string; brandShortName: string; defaultProjectId: string }>("/app-config"),
  googleLogin: (credential: string) =>
    api.post<{ ok: boolean; email: string; name: string }>("/auth/google", { credential }),
  getGoogleClientId: () =>
    api.get<{ clientId: string }>("/auth/google-client-id"),
  me: () => api.get<{ email: string; name: string; role: "admin" | "editor" | "viewer"; projectId: string }>("/auth/me"),
  logout: () => api.post<{ ok: boolean }>("/auth/logout", {}),
};

export const projectsApi = {
  list: () => api.get<{ data: Project[] }>("/projects"),
  switch: (projectId: string) =>
    api.post<{ ok: boolean; project: Project }>("/projects/switch", { projectId }),
  update: (id: string, data: {
    faxAccountId?: number | null;
    emailDeliveryPaused?: boolean;
    emailDeliveryMaxRecipients?: number;
    emailDeliveryMaxCampaignRecipients?: number;
    emailDeliveryBatchObservationMinutes?: number;
  }) =>
    api.put<{ ok: boolean; project: Project }>(`/projects/${id}`, data),
};

export const contactsApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<Contact>>(`/contacts?${qs}`);
  },
  get: (id: string) => api.get<ContactDetail>(`/contacts/${id}`),
  patch: (id: string, data: Record<string, unknown>) => api.patch<ContactDetail>(`/contacts/${id}`, data),
  webEvents: (id: string) => api.get<{ data: WebEvent[] }>(`/contacts/${id}/web-events`),
  faxReceived: (id: string) => api.get<{ data: FaxReceived[] }>(`/contacts/${id}/fax-received`),
  timeline: (id: string, params?: { cursor?: string; limit?: number; channels?: string[] }) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.channels?.length) qs.set("channels", params.channels.join(","));
    return api.get<TimelineResponse>(`/contacts/${id}/timeline?${qs}`);
  },
  lineMessages: (id: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<LineMessage>>(`/contacts/${id}/line-messages?${qs}`);
  },
};

export const faxReceivedApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<FaxReceived>>(`/fax/received?${qs}`);
  },
  get: (id: string) => api.get<FaxReceived>(`/fax/received/${id}`),
  pdfUrl: (id: string) => `/api/fax/received/${id}/pdf`,
};

export const campaignsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: Campaign[] }>(`/campaigns?${qs}`);
  },
  create: (data: {
    name: string;
    subject: string;
    htmlBody: string;
    textBody?: string;
    channel?: string;
    segmentQuery?: unknown;
    emailTemplateId?: string | null;
    audienceListId?: string | null;
    faxTemplateKey?: string | null;
    faxVariantKey?: string | null;
    faxSenderName?: string | null;
    faxSenderPhone?: string | null;
  }) => api.post<Campaign>("/campaigns", data),
  get: (id: string) => api.get<Campaign>(`/campaigns/${id}`),
  update: (id: string, data: Partial<Campaign>) =>
    api.put<Campaign>(`/campaigns/${id}`, data),
  send: (id: string, expectedCount: number) =>
    api.post<{ ok: boolean; totalSends: number; expectedCount: number }>(`/campaigns/${id}/send`, { expectedCount }),
  schedule: (id: string, scheduledAt: string, expectedCount: number) =>
    api.post<{ ok: boolean; expectedCount: number }>(`/campaigns/${id}/schedule`, { scheduledAt, expectedCount }),
  testDeliveryOptions: () =>
    api.get<{ recipients: string[]; configured: boolean; maxRecipients: number; allowedDomains: string[] }>("/test-delivery-options"),
  addTestDeliveryRecipient: (email: string) =>
    api.post<{ recipients: string[]; configured: boolean; maxRecipients: number; allowedDomains: string[]; added: string }>(
      "/test-delivery-options/recipients",
      { email },
    ),
  testDeliveries: (id: string, recipients: string[]) =>
    api.post<{
      ok: boolean;
      sent: number;
      deliveries: Array<{ id: string; targetHash: string; contentHash: string; sesMessageId: string | null; status: string; error: string | null }>;
    }>(`/campaigns/${id}/test-deliveries`, { recipients }),
  cancel: (id: string) =>
    api.post<{ ok: boolean; cancelled: number }>(`/campaigns/${id}/cancel`, {}),
  clone: (id: string) =>
    api.post<Campaign>(`/campaigns/${id}/clone`, {}),
  uploadFaxPdf: async (id: string, file: File): Promise<{ ok: boolean; faxDocumentId: number }> => {
    const formData = new FormData();
    formData.append("pdf", file);
    const res = await fetch(`/api/campaigns/${id}/fax-pdf`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(err.error || "Upload failed");
    }
    return res.json();
  },
  sends: (id: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<CampaignSendDetail>>(`/campaigns/${id}/sends?${qs}`);
  },
  conversions: (id: string) =>
    api.get<{
      data: Array<import("@/types").ConversionEvent & { contact?: { email: string; company: string | null; lastName: string | null } }>;
      totalRevenue: number;
      totalCount: number;
    }>(`/campaigns/${id}/conversions`),
};

export const segmentsApi = {
  preview: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<{ count: number }>(`/segments/preview?${qs}`);
  },
  contacts: (params: Record<string, string>, page = 1, limit = 50) => {
    const qs = new URLSearchParams({ ...params, page: String(page), limit: String(limit) }).toString();
    return api.get<{ data: Array<{ id: string; email: string; lastName?: string; firstName?: string; company?: string; traderType?: string }>; total: number }>(`/segments/contacts?${qs}`);
  },
};

export const audienceListsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: AudienceList[] }>(`/audience-lists?${qs}`);
  },
  create: (data: {
    name: string;
    description?: string | null;
    targetType?: "CONTACT" | "PROSPECT";
    mode?: "DYNAMIC" | "STATIC" | "SNAPSHOT";
    baseFilter?: SegmentFilter | null;
    contactIds?: string[];
  }) => api.post<AudienceList>("/audience-lists", data),
  get: (id: string) => api.get<AudienceList>(`/audience-lists/${id}`),
  members: (id: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<AudienceListMember>>(`/audience-lists/${id}/members?${qs}`);
  },
  updateMembers: (id: string, data: {
    includeIds?: string[];
    includeEmails?: string[];
    includeContacts?: Array<{
      email: string;
      company?: string;
      lastName?: string;
      firstName?: string;
    }>;
    excludeIds?: string[];
    restoreIds?: string[];
    note?: string;
  }) => api.post<{ ok: boolean; included: number; excluded: number; restored: number }>(`/audience-lists/${id}/members`, data),
  performance: (id: string) =>
    api.get<NewsletterPerformance>(`/audience-lists/${id}/performance`),
  createCampaign: (id: string, data: {
    emailTemplateId: string;
    name: string;
    fromName?: string;
    fromEmail?: string;
  }) => api.post<Campaign>(`/audience-lists/${id}/create-campaign`, data),
};

export const prospectsApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<Prospect>>(`/prospects?${qs}`);
  },
  refreshCandidates: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<ProspectRefreshCandidatesResponse>(`/prospects/refresh-candidates${qs ? `?${qs}` : ""}`);
  },
  executableSummary: () =>
    api.get<ProspectExecutableSummary>("/prospects/executable-summary"),
  callQueue: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<ProspectCallQueueResponse>(`/prospects/call-queue${qs ? `?${qs}` : ""}`);
  },
  reconcile: (data?: { limit?: number; listSource?: string }) =>
    api.post<{
      ok: boolean;
      total: number;
      linked: number;
      noMatch: number;
      conflict: number;
      samples: Array<{
        prospectId: string;
        company: string;
        result: string;
        contactId?: string;
        matchedBy?: string;
      }>;
    }>("/prospects/reconcile", data || {}),
  importCsv: async (file: File, data: {
    listSource: string;
    listVersion: string;
    mode: "append" | "refresh";
    dryRun?: boolean;
  }) => {
    const formData = new FormData();
    formData.append("csv", file);
    formData.append("listSource", data.listSource);
    formData.append("listVersion", data.listVersion);
    formData.append("mode", data.mode);
    if (data.dryRun) formData.append("dryRun", "true");
    const res = await fetch("/api/prospects/import", {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Import failed" }));
      throw new Error(err.error || "Import failed");
    }
    return res.json() as Promise<{
      ok: boolean;
      mode: "append" | "refresh";
      dryRun: boolean;
      listSource: string;
      listVersion: string;
      created: number;
      updated: number;
      skipped: number;
      linked: number;
      total: number;
      matchedBy: Record<string, number>;
      errors: string[];
    }>;
  },
};

export interface EmailTemplateSummary {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  kind: "GENERIC" | "NEWSLETTER" | "FOLLOWUP";
  status: "DRAFT" | "REVIEW" | "APPROVED" | "ARCHIVED";
  topicKey: string | null;
  sourcePath: string | null;
  updatedAt: string;
  _count?: { campaigns: number };
}

export interface EmailTemplate extends EmailTemplateSummary {
  textBody: string | null;
  notes: string | null;
}

export interface NewsletterPerformance {
  campaigns: number;
  sends: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  openRate: number;
  clickRate: number;
  lastSentAt: string | null;
}

export interface StatsOverview {
  totalContacts: number;
  contactsByStatus: Record<string, number>;
  contactsBySource: Record<string, number>;
  recentCampaigns: {
    id: string;
    name: string;
    sentAt: string | null;
    totalSent: number;
    totalBounced: number;
    opened: number;
    clicked: number;
    openRate: number;
    clickRate: number;
  }[];
  conversionRevenue30d?: number;
  conversionCount30d?: number;
}

export const templatesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: EmailTemplateSummary[] }>(`/templates?${qs}`);
  },
  get: (id: string) => api.get<EmailTemplate>(`/templates/${id}`),
  create: (data: {
    name: string;
    subject: string;
    htmlBody: string;
    textBody?: string;
    kind?: "GENERIC" | "NEWSLETTER" | "FOLLOWUP";
    status?: "DRAFT" | "REVIEW" | "APPROVED" | "ARCHIVED";
    sourcePath?: string | null;
    topicKey?: string | null;
    notes?: string | null;
  }) =>
    api.post<EmailTemplate>("/templates", data),
  update: (id: string, data: Partial<{
    name: string;
    subject: string;
    htmlBody: string;
    textBody?: string;
    kind: "GENERIC" | "NEWSLETTER" | "FOLLOWUP";
    status: "DRAFT" | "REVIEW" | "APPROVED" | "ARCHIVED";
    sourcePath: string | null;
    topicKey: string | null;
    notes: string | null;
  }>) =>
    api.put<EmailTemplate>(`/templates/${id}`, data),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/templates/${id}`),
  performance: (id: string) =>
    api.get<NewsletterPerformance>(`/templates/${id}/performance`),
  createCampaign: (id: string, data: {
    audienceListId: string;
    name: string;
    fromName?: string;
    fromEmail?: string;
  }) => api.post<Campaign>(`/templates/${id}/create-campaign`, data),
  generate: (prompt: string) =>
    api.post<{ name: string; subject: string; htmlBody: string; textBody: string }>("/templates/generate", { prompt }),
};

export interface HotLead {
  id: string;
  email: string;
  company: string | null;
  lastName: string | null;
  firstName: string | null;
  leadScore: number;
  lastEngagedAt: string | null;
  traderRank: string | null;
  source: string;
}

export interface ChannelBreakdown {
  channels: Record<string, { total: number; sent: number; bounced: number }>;
}

export const statsApi = {
  overview: () => api.get<StatsOverview>("/stats/overview"),
  hotLeads: () => api.get<{ data: HotLead[] }>("/stats/hot-leads"),
  channels: () => api.get<ChannelBreakdown>("/stats/channels"),
};

export const faxImportApi = {
  upload: async (file: File): Promise<{
    ok: boolean;
    created: number;
    skipped: number;
    merged: number;
    total: number;
    errors: string[];
  }> => {
    const formData = new FormData();
    formData.append("csv", file);
    const res = await fetch("/api/fax-import", {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Import failed" }));
      throw new Error(err.error || "Import failed");
    }
    return res.json();
  },
};

export const syncApi = {
  contacts: () => api.post<{ upserted: number; skipped: number }>("/sync/contacts", {}),
  unsubscribes: () => api.post<{ count: number }>("/sync/unsubscribes", {}),
};

export const callCampaignsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: CallCampaign[] }>(`/call-campaigns?${qs}`);
  },
  create: (data: {
    name: string;
    segmentQuery?: SegmentFilter | ProspectSegmentFilter;
    audienceType?: AudienceType;
    script: {
      name: string;
      greeting: string;
      productName: string;
      productPitch: string;
      callbackMessage?: string;
      farewellMessage?: string;
      companyName?: string;
      ttsProvider?: "openai" | "google";
      ttsModel?: string;
      ttsVoice?: string;
      ttsInstructions?: string;
      turns?: unknown;
    };
    emailCampaignId?: string;
    triggerRule?: unknown;
  }) => api.post<CallCampaign>("/call-campaigns", data),
  importChannelExport: (data: {
    exportUrl?: string;
    payload?: unknown;
    campaignName?: string;
    dryRun?: boolean;
  }) => api.post<ChannelExportImportResult | { ok: boolean; dryRun: true; summary: ChannelExportImportSummary }>(
    "/call-campaigns/channel-export/import",
    data,
  ),
  get: (id: string) => api.get<CallCampaign>(`/call-campaigns/${id}`),
  liveStatus: (id: string) => api.get<import("@/types").CallCampaignLiveStatus>(`/call-campaigns/${id}/live-status`),
  update: (id: string, data: Record<string, unknown>) =>
    api.put<CallCampaign>(`/call-campaigns/${id}`, data),
  start: (id: string) =>
    api.post<{ ok: boolean; totalCalls: number }>(`/call-campaigns/${id}/start`, {}),
  schedule: (id: string, scheduledAt: string) =>
    api.post<{ ok: boolean; scheduledAt: string }>(`/call-campaigns/${id}/schedule`, { scheduledAt }),
  pause: (id: string) =>
    api.post<{ ok: boolean }>(`/call-campaigns/${id}/pause`, {}),
  resume: (id: string) =>
    api.post<{ ok: boolean }>(`/call-campaigns/${id}/resume`, {}),
  cancel: (id: string) =>
    api.post<{ ok: boolean; cancelled: number }>(`/call-campaigns/${id}/cancel`, {}),
  cloneRemaining: (id: string) =>
    api.post<CallCampaign>(`/call-campaigns/${id}/clone-remaining`, {}),
  cloneRedial: (id: string, outcomes: import("@/types").CallOutcome[]) =>
    api.post<CallCampaign>(`/call-campaigns/${id}/clone-redial`, { outcomes }),
  results: (id: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<CallResult>>(`/call-campaigns/${id}/results?${qs}`);
  },
  recordingUrl: (resultId: string) =>
    api.get<import("@/types").CallRecordingPlayback>(`/call-results/${resultId}/recording-url`),
  segmentPreview: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<{ total: number; withPhone: number }>(`/call-campaigns/segment-preview?${qs}`);
  },
  testCall: (id: string, data: {
    phone: string;
    name?: string;
    company?: string;
    ttsProvider?: "openai" | "google";
    ttsModel?: string;
    ttsVoice?: string;
    ttsInstructions?: string;
  }) =>
    api.post<{ ok: boolean; testId: string }>(`/call-campaigns/${id}/test-call`, data),
  ttsPreview: async (id: string, data: {
    name?: string;
    company?: string;
    ttsProvider?: "openai" | "google";
    ttsModel?: string;
    ttsVoice?: string;
    ttsInstructions?: string;
  }) => {
    const res = await fetch(`/api/call-campaigns/${id}/tts-preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "TTS preview failed" }));
      throw new Error(err.message || err.error || "TTS preview failed");
    }
    return res.blob();
  },
  generateFlow: (prompt: string) =>
    api.post<{ nodes: import("@/types").TurnNode[] }>("/call-campaigns/generate-flow", { prompt }),
};

export const callResultsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<CallResult & { callCampaign?: { name: string } }>>(`/call-results?${qs}`);
  },
};

export const smsTemplatesApi = {
  list: () => api.get<{ data: SmsTemplate[] }>("/sms-templates"),
  get: (id: string) => api.get<SmsTemplate>(`/sms-templates/${id}`),
  create: (data: { name: string; body: string }) =>
    api.post<SmsTemplate>("/sms-templates", data),
  update: (id: string, data: Partial<{ name: string; body: string }>) =>
    api.put<SmsTemplate>(`/sms-templates/${id}`, data),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/sms-templates/${id}`),
};

export const lineTemplatesApi = {
  list: () => api.get<{ data: LineTemplate[] }>("/line-templates"),
  get: (id: string) => api.get<LineTemplate>(`/line-templates/${id}`),
  create: (data: { name: string; type?: string; body?: string; altText?: string; flexJson?: Record<string, unknown> }) =>
    api.post<LineTemplate>("/line-templates", data),
  update: (id: string, data: Partial<{ name: string; type: string; body: string; altText: string; flexJson: Record<string, unknown> }>) =>
    api.put<LineTemplate>(`/line-templates/${id}`, data),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/line-templates/${id}`),
};

export const metaApi = {
  settings: () => api.get<MetaSettings>("/meta/settings"),
  testConnection: () =>
    api.post<{ adAccount: { id: string; name: string } | null; pixel: { id: string; name: string } | null }>("/meta/test", {}),
  listAudiences: () => api.get<{ data: MetaAudience[] }>("/meta/audiences"),
  createAudience: (data: { name: string; segmentQuery?: unknown; adAccountId?: string }) =>
    api.post<MetaAudience>("/meta/audiences", data),
  getAudience: (id: string) => api.get<MetaAudience>(`/meta/audiences/${id}`),
  updateAudience: (id: string, data: { name?: string; segmentQuery?: unknown; syncStatus?: string }) =>
    api.put<MetaAudience>(`/meta/audiences/${id}`, data),
  syncAudience: (id: string) =>
    api.post<{ ok: boolean; audience: MetaAudience }>(`/meta/audiences/${id}/sync`, {}),
  deleteAudience: (id: string) => api.delete<{ ok: boolean }>(`/meta/audiences/${id}`),
  capiLogs: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: MetaConversionLog[] }>(`/meta/capi/logs?${qs}`);
  },
};

export const sequencesApi = {
  list: () => api.get<{ data: Sequence[] }>("/sequences"),
  create: (data: {
    name: string;
    triggerType: string;
    triggerConfig?: Record<string, unknown> | null;
    description?: string | null;
    steps: Array<{
      stepNumber: number;
      delayDays: number;
      channel?: string;
      emailTemplateId?: string | null;
      smsTemplateId?: string | null;
      lineTemplateId?: string | null;
      faxDocumentId?: number | null;
      subject?: string | null;
      condition?: { branch: string; ifTrue?: number; ifFalse?: number } | null;
    }>;
  }) => api.post<Sequence>("/sequences", data),
  get: (id: string) => api.get<Sequence>(`/sequences/${id}`),
  update: (id: string, data: Record<string, unknown>) =>
    api.put<Sequence>(`/sequences/${id}`, data),
  activate: (id: string) =>
    api.post<{ ok: boolean }>(`/sequences/${id}/activate`, {}),
  pause: (id: string) =>
    api.post<{ ok: boolean }>(`/sequences/${id}/pause`, {}),
  enroll: (id: string, contactIds: string[]) =>
    api.post<{ ok: boolean; enrolled: number }>(`/sequences/${id}/enroll`, { contactIds }),
  enrollments: (id: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<SequenceEnrollment>>(`/sequences/${id}/enrollments?${qs}`);
  },
  exitEnrollment: (seqId: string, enrollmentId: string) =>
    api.post<{ ok: boolean }>(`/sequences/${seqId}/enrollments/${enrollmentId}/exit`, {}),
  testSend: (data: { sequenceId: string; stepNumber: number; email: string }) =>
    api.post<{ ok: boolean }>("/sequences/test-send", data),
  generate: (prompt: string) =>
    api.post<{
      name: string;
      description: string;
      triggerType: string;
      steps: Array<{
        stepNumber: number;
        delayDays: number;
        subject: string;
        bodyOutline: string;
        condition?: { branch: string; ifTrue?: number; ifFalse?: number } | null;
      }>;
    }>("/sequences/generate", { prompt }),
};

export const doNotCallApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<DoNotCallEntry>>(`/do-not-call?${qs}`);
  },
  add: (contactId: string, note?: string) =>
    api.post<{ ok: boolean; contact: Contact }>("/do-not-call", { contactId, note }),
  remove: (contactId: string) =>
    api.delete<{ ok: boolean; contact: Contact }>(`/do-not-call/${contactId}`),
};

export const aiManagerApi = {
  status: () => api.get<any>("/ai-manager/status"),
};
